import React, { useState, useRef, useEffect } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { CRow, CCol } from "@coreui/react";

/**
 * Componente de editor de texto baseado no TinyMCE.
 *
 * @param {string} label - Rótulo exibido acima do editor.
 * @param {string} value - Valor inicial do editor.
 * @param {number} maxLength - Número máximo de caracteres permitidos.
 * @param {function} onChange - Função chamada quando o conteúdo do editor muda.
 */
const TextEditor = ({ label, value, maxLength, onChange, className }) => {
  // Estado para contar caracteres digitados
  const [charCount, setCharCount] = useState("...");
  const isOverLimit = charCount > maxLength;

  // Referência para o editor
  const editorRef = useRef(null);

  // Estado para armazenar o tema atual
  const [theme, setTheme] = useState(
    document.documentElement.getAttribute("data-coreui-theme")
  );

  // Chave para forçar re-renderização do editor quando o tema mudar
  const [key, setKey] = useState(0);

  /**
   * Manipula as mudanças no editor, atualizando a contagem de caracteres e chamando o callback externo.
   * @param {string} content - Conteúdo atual do editor.
   * @param {object} editor - Instância do editor TinyMCE.
   */
  const handleEditorChange = (content, editor) => {
    if (maxLength) {
      // Obtém o conteúdo como texto puro e conta os caracteres, ignorando quebras de linha
      const text_content = editor.getContent({ format: "text" });
      const currentLength = text_content
        .replace(/\n\n/g, ".")
        .replace(/\n/g, "").length;
      setCharCount(currentLength);
    }

    // Dispara a função de callback caso exista
    if (onChange) {
      onChange(content);
    }
  };

  /**
   * Efeito que observa mudanças no tema da aplicação e força a atualização do editor.
   */
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const newTheme =
        document.documentElement.getAttribute("data-coreui-theme");
      if (newTheme !== theme) {
        setTheme(newTheme);
        setKey(key + 1); // Atualiza a chave para forçar re-renderização do editor
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-coreui-theme"],
    });

    return () => observer.disconnect();
  }, [theme]);

  return (
    <>
      {/* Linha superior com o rótulo e a contagem de caracteres */}
      <CRow className="mb-2">
        <CCol md={11} className="fw-bold">
          {label}
        </CCol>
        {maxLength && (
          <CCol md={1} className="text-end">
            <span
              style={{ fontSize: "12px", color: isOverLimit ? "red" : "gray" }}
            >
              {charCount}/{maxLength}
            </span>
          </CCol>
        )}
      </CRow>

      {/* Editor de texto TinyMCE */}
      <div className={className}>
        <Editor
          key={key} // Força recriação do editor ao mudar o tema
          tinymceScriptSrc="/tinymce/tinymce.min.js" // Caminho para o script TinyMCE
          onInit={(_evt, editor) => {
            editorRef.current = editor;
            // Atualiza a contagem de caracteres assim que o editor está pronto
            if (maxLength) {
              const text_content = editor.getContent({ format: "text" });
              const currentLength = text_content
                .replace(/\n\n/g, ".")
                .replace(/\n/g, "").length;
              setCharCount(currentLength);
            }
          }}
          value={value} // Valor inicial do editor
          init={{
            license_key: "gpl",
            height: 400,
            menubar: false,
            branding: false,
            promotion: false,
            browser_spellcheck: true,
            link_default_target: "_blank",
            plugins: ["autolink", "searchreplace", "wordcount"],
            paste_preprocess: function (plugin, args) {
              const hasImage = /<img\s[^>]*>|data:image\//i.test(args.content);

              if (hasImage) {
                args.stopImmediatePropagation();
                args.stopPropagation();
                args.preventDefault();
              }
            },
            toolbar:
              "undo redo | bold italic forecolor backcolor | " +
              "alignleft aligncenter alignright alignjustify | " +
              "bullist numlist outdent indent | removeformat",
            skin: theme === "dark" ? "oxide-dark" : "oxide",
            content_css: theme === "dark" ? "dark" : "default",
            content_style:
              "body { font-family:Helvetica,Arial,sans-serif; font-size:16px } p {margin-top:5px; margin-bottom:5px}",
          }}
          onEditorChange={handleEditorChange}
        />
      </div>
    </>
  );
};

export default TextEditor;
